import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Bell as FaBell,
	Brain as FaBrain,
	ChartColumn as FaChartBar,
	CheckCheck as FaCheckAll,
	Mail as FaEnvelope,
	Star as FaStar,
	TriangleAlert as FaExclamationTriangle,
} from "lucide-react";
import {
	getNotificationsApi,
	getUnreadCountApi,
	markAllNotificationsReadApi,
	markNotificationReadApi,
} from "../../api/notifications.api";
import useNotificationStore from "../../store/notificationStore";
import type { Notification } from "../../types/notification";

const TYPE_NAV: Record<string, string> = {
	new_review: "/dashboard/reviews",
	crisis_alert: "/dashboard/crisis-alerts",
	competitor: "/dashboard/competitors",
	ai_visibility: "/dashboard/aeo-insights",
	weekly_digest: "/dashboard/overview",
};

function notificationIcon(type: string) {
	const cls = "shrink-0";
	switch (type) {
		case "new_review":
			return <FaStar size={14} className={cls} />;
		case "crisis_alert":
			return <FaExclamationTriangle size={14} className={cls} />;
		case "competitor":
			return <FaChartBar size={14} className={cls} />;
		case "ai_visibility":
			return <FaBrain size={14} className={cls} />;
		case "weekly_digest":
			return <FaEnvelope size={14} className={cls} />;
		default:
			return <FaBell size={14} className={cls} />;
	}
}

function timeAgo(dateStr: string) {
	const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
	if (diff < 60) return "just now";
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	return `${Math.floor(diff / 86400)}d ago`;
}

interface BellTheme {
	primary: string;
	border: string;
	borderDark?: string;
	danger: string;
	card: string;
	text: string;
	textMuted: string;
	infoBg: string;
}

const DEFAULT_THEME: BellTheme = {
	primary: "#0222F1",
	border: "rgba(2,34,241,0.10)",
	danger: "#EF4444",
	card: "#FFFFFF",
	text: "#0F172A",
	textMuted: "#64748B",
	infoBg: "rgba(2,34,241,0.08)",
};

export default function NotificationBell({ theme = DEFAULT_THEME }: { theme?: BellTheme }) {
	const navigate = useNavigate();
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [isMarkingAll, setIsMarkingAll] = useState(false);

	const {
		notifications,
		unreadCount,
		setNotifications,
		setUnreadCount,
		markRead,
		markAllRead,
		decrementUnreadCount,
	} = useNotificationStore();

	useEffect(() => {
		const fetchInitial = async () => {
			try {
				const [countRes, listRes] = await Promise.all([
					getUnreadCountApi(),
					getNotificationsApi({ page: 1, limit: 5 }),
				]);
				setUnreadCount(countRes?.data?.count ?? 0);
				setNotifications(listRes?.data?.data || []);
			} catch {
				// Silently fail — bell stays with last known state.
			}
		};

		fetchInitial();

		const interval = setInterval(async () => {
			try {
				const countRes = await getUnreadCountApi();
				setUnreadCount(countRes?.data?.count ?? 0);
			} catch {
				// no-op
			}
		}, 900_000);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleNotificationClick = async (notification: Notification) => {
		setOpen(false);
		if (!notification.isRead) {
			markRead(notification.id);
			decrementUnreadCount();
			try {
				await markNotificationReadApi(notification.id);
			} catch {
				// no-op
			}
		}
		const path = TYPE_NAV[notification.type] || "/dashboard/notifications";
		navigate(path);
	};

	const handleMarkAllRead = async () => {
		if (isMarkingAll) return;
		try {
			setIsMarkingAll(true);
			await markAllNotificationsReadApi();
			markAllRead();
		} catch {
			// no-op
		} finally {
			setIsMarkingAll(false);
		}
	};

	const badgeCount = unreadCount > 99 ? "99+" : unreadCount;
	const recentFive = notifications.slice(0, 5);

	return (
		<div ref={dropdownRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="relative flex h-11 w-11 items-center justify-center rounded-2xl transition"
				style={{
					border: `1.5px solid ${theme.borderDark || theme.border}`,
					background: theme.card,
				}}
				aria-label="Notifications"
			>
				<FaBell size={14} color={theme.primary} />

				{unreadCount > 0 && (
					<span className="absolute -right-1 -top-1 flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
						{badgeCount}
					</span>
				)}
			</button>

			{open && (
				<div
					className="absolute right-0 top-[52px] z-50 w-[340px] rounded-[24px] border shadow-[0_18px_45px_rgba(15,23,42,0.14)]"
					style={{ borderColor: theme.border, background: theme.card }}
				>
					{/* Header */}
					<div
						className="flex items-center justify-between border-b px-4 py-3"
						style={{ borderColor: theme.border }}
					>
						<p className="text-sm font-bold" style={{ color: theme.text }}>
							Notifications
							{unreadCount > 0 && (
								<span
									className="ml-2 rounded-full px-2 py-0.5 text-xs font-bold"
									style={{ background: theme.infoBg, color: theme.primary }}
								>
									{unreadCount} unread
								</span>
							)}
						</p>

						{unreadCount > 0 && (
							<button
								type="button"
								onClick={handleMarkAllRead}
								disabled={isMarkingAll}
								className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0222F1] hover:opacity-75 disabled:opacity-50"
							>
								<FaCheckAll size={12} />
								{isMarkingAll ? "Marking..." : "Mark all read"}
							</button>
						)}
					</div>

					{/* List */}
					<div className="max-h-[320px] overflow-y-auto">
						{recentFive.length === 0 ? (
							<div className="px-4 py-8 text-center">
								<FaBell size={24} className="mx-auto text-slate-300" />
								<p className="mt-3 text-sm text-slate-400">
									No notifications yet
								</p>
							</div>
						) : (
							recentFive.map((notification) => (
								<button
									key={notification.id}
									type="button"
									onClick={() => handleNotificationClick(notification)}
									className={`flex w-full items-start gap-3 border-b px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50 ${
										!notification.isRead ? "bg-[#0222F1]/[0.03]" : ""
									}`}
									style={{ borderColor: theme.border }}
								>
									<div
										className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
										style={{
											background:
												notification.priority === "critical"
													? "rgba(239,68,68,0.12)"
													: notification.priority === "important"
														? "rgba(245,158,11,0.12)"
														: "rgba(2,34,241,0.08)",
											color:
												notification.priority === "critical"
													? "#EF4444"
													: notification.priority === "important"
														? "#F59E0B"
														: "#0222F1",
										}}
									>
										{notificationIcon(notification.type)}
									</div>

									<div className="min-w-0 flex-1">
										<p className="text-sm font-semibold text-[#0F172A]">
											{notification.title}
										</p>
										<p className="mt-0.5 truncate text-xs text-slate-500">
											{notification.message}
										</p>
										<p className="mt-1 text-xs text-slate-400">
											{timeAgo(notification.createdAt)}
										</p>
									</div>

									{!notification.isRead && (
										<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0222F1]" />
									)}
								</button>
							))
						)}
					</div>

					{/* Footer */}
					<div
						className="border-t px-4 py-3"
						style={{ borderColor: theme.border }}
					>
						<button
							type="button"
							onClick={() => {
								setOpen(false);
								navigate("/dashboard/notifications");
							}}
							className="w-full rounded-2xl py-2.5 text-sm font-bold transition hover:opacity-80"
							style={{ background: theme.infoBg, color: theme.primary }}
						>
							View all notifications →
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
